/**
 * SDK Error Hierarchy (§80 §8.1)
 *
 * Extends EngineException with SDK-specific error codes.
 * All SDK operations throw SdkError (or subclass) on failure.
 */

import type { EngineErrorCode, EngineException } from './types'

// ── SDK-specific error codes ──────────────────────────────────────
export type SdkErrorCode = EngineErrorCode
    | 'SDK_NOT_INITIALIZED'
    | 'SDK_INVALID_CHARSET'
    | 'WORKSPACE_CLOSED'
    | 'WRITE_FORBIDDEN'
    | 'FILE_NOT_FOUND'
    | 'CONFIG_BLOB_UNAVAILABLE'
    | 'INVALID_TOKEN'
    | 'INVALID_REQUEST'
    | 'INVALID_PATH'
    | 'BUILD_FAILED'
    | 'TIMEOUT'
    | 'SYNC_FAILED'

/**
 * Top-level SDK error. Wraps engine errors and adds SDK-specific codes.
 */
export class SdkError extends Error {
    readonly code: SdkErrorCode
    readonly retryable: boolean
    readonly cause?: unknown

    constructor(code: SdkErrorCode, message: string, options?: { retryable?: boolean; cause?: unknown }) {
        super(message)
        this.name = 'SdkError'
        this.code = code
        this.retryable = options?.retryable ?? false
        this.cause = options?.cause
    }

    /** Wrap an EngineException into SdkError, preserving the code. */
    static fromEngine(err: EngineException): SdkError {
        return new SdkError(err.error.code, err.error.message, {
            cause: err,
            retryable: err.error.retryable,
        })
    }
}
